import React from 'react';
import { useAppSelector } from '../../store/hooks';
import {
  selectCanAccessPage,
  selectCanAccessFeature,
  selectCanCreateAgent,
  selectCombinedPermissions,
} from '../../store/slices/permissionsSlice';

// HOC for page-level permissions
export const withPagePermissions = (WrappedComponent, requiredPage) => {
  return function PermissionWrappedComponent(props) {
    const canAccess = useCanAccessPage(requiredPage);
    const permissions = useAppSelector(selectCombinedPermissions);

    if (!permissions) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
        </div>
      );
    }

    if (!canAccess) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="text-center">
            <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-red-100 flex items-center justify-center">
              <svg
                className="h-8 w-8 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
            <p className="text-gray-600 mb-4">You don't have permission to access this page.</p>
            <button
              onClick={() => window.history.back()}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
            >
              Go Back
            </button>
          </div>
        </div>
      );
    }

    return <WrappedComponent {...props} />;
  };
};

// HOC for feature-level permissions
export const withFeaturePermissions = (WrappedComponent, requiredFeature) => {
  return function FeaturePermissionWrappedComponent(props) {
    const canAccess = useCanAccessFeature(requiredFeature);
    const permissions = useAppSelector(selectCombinedPermissions);

    if (!permissions) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
        </div>
      );
    }

    if (!canAccess) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="text-center">
            <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-red-100 flex items-center justify-center">
              <svg
                className="h-8 w-8 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Feature Not Available</h2>
            <p className="text-gray-600 mb-4">
              This feature is not available in your current plan.
            </p>
            <button
              onClick={() => window.history.back()}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
            >
              Go Back
            </button>
          </div>
        </div>
      );
    }

    return <WrappedComponent {...props} />;
  };
};

// Custom hook for page access check
export const useCanAccessPage = page => {
  return useAppSelector(state => selectCanAccessPage(state, page));
};

// Custom hook for feature access check
export const useCanAccessFeature = feature => {
  return useAppSelector(state => selectCanAccessFeature(state, feature));
};

// Hook for permission checks
export const usePermissions = () => {
  const canCreateAgent = useAppSelector(selectCanCreateAgent);
  const permissions = useAppSelector(selectCombinedPermissions);

  return {
    canAccessPage: useCanAccessPage,
    canAccessFeature: useCanAccessFeature,
    canCreateAgent,
    permissions,
    isFreemium:
      permissions?.planName?.includes('Freemium') || permissions?.planName?.includes('Trial'),
    maxAgents: permissions?.maxAgents || 0,
  };
};

export default {
  withPagePermissions,
  withFeaturePermissions,
  usePermissions,
  useCanAccessPage,
  useCanAccessFeature,
};
